import React, { forwardRef } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  EmcurePieTooltip,
  formatChartMetricValue,
  formatChartTooltipValue,
} from "../../ChatPage/ChatSectionChartHelpers";
import { EMCURE_PIE_COLORS, humanizeColumnLabel } from "./emcureChartConstants";
import ChartSeriesLines from "./ChartSeriesLines";

/**
 * Fixed-width bar / line / pie inside the admin "view messages" modal (per chart card).
 */
const EmcureAdminInlineChartPlot = forwardRef(function EmcureAdminInlineChartPlot(
  {
    chartType,
    validData,
    chart,
    cIdx,
    formatDateAxis,
    parseTooltipNumber,
    formatCategoryLabel,
  },
  ref
) {
  const yCol = chart?.y_column;
  const seriesKeys = chart?.seriesKeys;

  return (
    <div ref={ref} className="chart-container">
      {chartType === "bar" && (
        <BarChart
          data={validData}
          width={1020}
          height={350}
          margin={{
            top: 20,
            right: 30,
            left: 30,
            bottom: 80,
          }}
        >
          <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" strokeOpacity={0.6} vertical={false} />
          <XAxis
            dataKey="xValue"
            angle={-45}
            textAnchor="end"
            interval="preserveStartEnd"
            height={80}
            tick={{
              fontSize: 10,
              fontWeight: 500,
              fill: "#374151",
            }}
            stroke="#9CA3AF"
            strokeWidth={1.5}
            tickFormatter={(value) => {
              const formatted = formatDateAxis(value);
              const label = formatted !== value ? String(formatted) : String(value ?? "");
              return label.length > 15 ? label.substring(0, 12) + "..." : label;
            }}
          />
          <YAxis
            width={120}
            tick={{
              fontSize: 11,
              fontWeight: 600,
              fill: "#374151",
            }}
            stroke="#9CA3AF"
            strokeWidth={1.5}
            tickFormatter={(value) => formatChartMetricValue(value)}
            tickMargin={5}
          />
          <Tooltip
            wrapperStyle={{
              pointerEvents: "none",
              outline: "none",
              zIndex: 50,
            }}
            allowEscapeViewBox={{ x: false, y: true }}
            contentStyle={{
              fontSize: "13px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "12px 16px",
              fontWeight: "600",
              maxWidth: "300px",
              wordWrap: "break-word",
            }}
            labelStyle={{
              color: "#111827",
              fontWeight: "700",
              marginBottom: "4px",
            }}
            labelFormatter={(label) => {
              if (label == null) return "";
              const formatted = formatDateAxis(label);
              return formatted !== label ? String(formatted) : String(label);
            }}
            formatter={(value) => [formatChartTooltipValue(value), humanizeColumnLabel(yCol)]}
            cursor={{ fill: "rgba(185, 0, 5, 0.1)" }}
            isAnimationActive={false}
            animationDuration={0}
          />
          <Bar dataKey="yValue" fill="#B90005" radius={[6, 6, 0, 0]} isAnimationActive={false} />
        </BarChart>
      )}

      {chartType === "line" && (
        <LineChart
          data={validData}
          width={1020}
          height={350}
          margin={{
            top: 20,
            right: 30,
            left: 30,
            bottom: 80,
          }}
        >
          <defs>
            <linearGradient id={`lineGradient-admin-${cIdx}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1E40AF" stopOpacity={1} />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.8} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="5 5" stroke="#E5E7EB" strokeOpacity={0.6} />
          <XAxis
            dataKey="xValue"
            angle={-45}
            textAnchor="end"
            interval="preserveStartEnd"
            height={80}
            tick={{
              fontSize: 10,
              fontWeight: 500,
              fill: "#374151",
            }}
            stroke="#9CA3AF"
            strokeWidth={1.5}
            tickFormatter={(value) => {
              const formatted = formatDateAxis(value);
              const label = formatted !== value ? String(formatted) : String(value ?? "");
              return label.length > 15 ? label.substring(0, 12) + "..." : label;
            }}
          />
          <YAxis
            width={120}
            tick={{
              fontSize: 11,
              fontWeight: 600,
              fill: "#374151",
            }}
            stroke="#9CA3AF"
            strokeWidth={1.5}
            tickFormatter={(value) => formatChartMetricValue(value)}
            tickMargin={5}
          />
          <Tooltip
            wrapperStyle={{
              pointerEvents: "none",
              outline: "none",
              zIndex: 50,
            }}
            allowEscapeViewBox={{ x: false, y: true }}
            contentStyle={{
              fontSize: "13px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "12px 16px",
              fontWeight: "600",
            }}
            labelFormatter={(label) => {
              if (label == null) return "";
              const formatted = formatDateAxis(label);
              return formatted !== label ? String(formatted) : String(label);
            }}
            formatter={(value, name) => [
              formatChartTooltipValue(value),
              seriesKeys?.length ? name : humanizeColumnLabel(yCol),
            ]}
            cursor={{ stroke: "#1E40AF", strokeWidth: 2 }}
            isAnimationActive={false}
            animationDuration={0}
          />
          {seriesKeys?.length ? (
            <ChartSeriesLines seriesKeys={seriesKeys} />
          ) : (
            <Line
              type="monotone"
              dataKey="yValue"
              stroke={`url(#lineGradient-admin-${cIdx})`}
              strokeWidth={3}
              dot={{ fill: "#1E40AF", r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      )}

      {chartType === "pie" && (
            <div className="admin-pie-chart-container">
              <div className="admin-pie-chart-visual">
                <PieChart width={600} height={460}>
                  <defs>
                    {validData.map((_, i) => (
                      <linearGradient
                        key={`pieGrad-admin-${cIdx}-${i}`}
                        id={`pieGrad-admin-${cIdx}-${i}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor={EMCURE_PIE_COLORS[i % EMCURE_PIE_COLORS.length]} stopOpacity={1} />
                        <stop offset="100%" stopColor={EMCURE_PIE_COLORS[i % EMCURE_PIE_COLORS.length]} stopOpacity={0.7} />
                      </linearGradient>
                    ))}
                  </defs>
                  <Tooltip
                    wrapperStyle={{
                      pointerEvents: "none",
                      outline: "none",
                      zIndex: 50,
                    }}
                    allowEscapeViewBox={{ x: false, y: true }}
                    content={
                      <EmcurePieTooltip
                        yColumn={yCol}
                        formatCategoryLabel={formatCategoryLabel}
                      />
                    }
                    isAnimationActive={false}
                    animationDuration={0}
                  />
                  <Pie
                    data={validData}
                    dataKey="yValue"
                    nameKey="xValue"
                    cx="45%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={150}
                    paddingAngle={validData.length > 20 ? 1 : 3}
                    label={({ cx, cy, midAngle, outerRadius: oR, percent, xValue }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = oR + 25;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      const pct = (percent * 100).toFixed(1);
                      const name = formatCategoryLabel(xValue);
                      const shortName = name.length > 10 ? name.substring(0, 8) + "…" : name;
                      return (
                        <text
                          x={x}
                          y={y}
                          textAnchor={x > cx ? "start" : "end"}
                          dominantBaseline="central"
                          fontSize={11}
                          fontWeight={500}
                          fill="#374151"
                        >
                          {`${shortName} ${pct}%`}
                        </text>
                      );
                    }}
                    labelLine={{ stroke: "#94A3B8", strokeWidth: 1, strokeDasharray: "3 3" }}
                    isAnimationActive={false}
                  >
                    {validData.map((_, i) => (
                      <Cell key={`cell-${i}`} fill={`url(#pieGrad-admin-${cIdx}-${i})`} />
                    ))}
                  </Pie>
                </PieChart>
              </div>
              <div className="admin-pie-chart-legend">
                {validData.map((entry, i) => (
                  <div key={i} className="admin-pie-legend-item">
                    <span
                      className="admin-pie-legend-dot"
                      style={{ backgroundColor: EMCURE_PIE_COLORS[i % EMCURE_PIE_COLORS.length] }}
                    />
                    <span className="admin-pie-legend-label" title={formatCategoryLabel(entry.xValue)}>
                      {(() => {
                        const disp = formatCategoryLabel(entry.xValue);
                        return disp.length > 20 ? disp.substring(0, 18) + "…" : disp;
                      })()}
                    </span>
                    <span className="admin-pie-legend-value">{formatChartMetricValue(entry.yValue)}</span>
                  </div>
                ))}
              </div>
            </div>
      )}
    </div>
  );
});

export default EmcureAdminInlineChartPlot;
